import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Heart, X, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1769650795569-e51a6e9580c3";

export default function LikesYou() {
  const [likes, setLikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/likes-you").then((r) => setLikes(r.data)).finally(() => setLoading(false));
  }, []);

  const respond = async (profile, direction) => {
    setLikes((prev) => prev.filter((p) => p.id !== profile.id));
    const { data } = await api.post("/swipe", { target_id: profile.id, direction });
    if (data.matched) {
      toast.success(`💛 It's a match with ${profile.name}!`, {
        action: { label: "Message", onClick: () => navigate(`/chat/${data.match_id}`) },
      });
    }
  };

  return (
    <div className="p-6 md:p-12 max-w-5xl mx-auto">
      <button data-testid="back-btn" onClick={() => navigate("/discover")} className="text-[#A0A0A0] hover:text-white flex items-center gap-2 mb-6">
        <ArrowLeft size={20} /> Back
      </button>
      <div className="overline mb-1">They made the first move</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-2">Who Likes You</h2>
      <p className="text-sm text-[#A0A0A0] mb-8">These members already liked you. Like them back to match instantly.</p>

      {loading ? <div className="text-[#A0A0A0]">Loading…</div> : likes.length === 0 ? (
        <div className="pe-card rounded-3xl p-10 text-center">
          <Heart className="text-[#D4AF37] mx-auto mb-4" size={40} />
          <h3 className="font-heading text-2xl mb-2">No likes yet</h3>
          <p className="text-sm text-[#A0A0A0]">Keep your profile enticing — likes will roll in.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {likes.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
              data-testid={`like-you-${p.id}`} className="pe-card rounded-2xl overflow-hidden">
              <div className="h-52 relative">
                <img src={p.photos?.[0] || FALLBACK} alt={p.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent 55%)" }} />
                <div className="absolute bottom-3 left-3">
                  <div className="font-heading text-2xl text-white">{p.name}{p.age ? `, ${p.age}` : ""}</div>
                  {p.location && <div className="text-xs text-[#A0A0A0] flex items-center gap-1"><MapPin size={12} /> {p.location}</div>}
                </div>
              </div>
              <div className="flex">
                <button data-testid={`likeback-pass-${p.id}`} onClick={() => respond(p, "pass")}
                  className="flex-1 py-3 text-[#E63946] flex items-center justify-center border-r border-[#2A2A2A] hover:bg-[#E63946]/10"><X size={20} /></button>
                <button data-testid={`likeback-like-${p.id}`} onClick={() => respond(p, "like")}
                  className="flex-1 py-3 text-black gold-gradient flex items-center justify-center hover:brightness-110"><Heart size={20} /></button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
